import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Lock, ArrowRight, CheckCircle2 } from 'lucide-react';
import { resolveApiBaseUrl } from '../../../utils/apiClient';
import AuthShell, { AuthField, AuthNotice, PasswordToggle, authLinkClass } from '../../../components/auth/AuthShell';
import Button from '../../../components/base/Button';

interface FieldErrors {
  newPassword?: string;
  confirmPassword?: string;
}

/** How long the success screen stays up before moving on to sign in. */
const REDIRECT_DELAY_MS = 2500;

/** Sign-in shows this key as its notice, in whichever language is active there. */
const SIGNIN_AFTER_RESET = { message: 'auth.passwordResetSuccess' };

/**
 * Whether /auth/confirm-reset refused the LINK rather than the password: a token
 * that is wrong, used or expired (its English `error` text), or an email/token the
 * validator rejected. Retyping cannot fix those; only a new link can.
 */
function isLinkRejected(status: number, body: { error?: unknown; details?: unknown }): boolean {
  if (status !== 400) return false;
  if (typeof body.error === 'string' && /invalid or expired|has expired/i.test(body.error)) return true;
  return (
    Array.isArray(body.details) &&
    body.details.some((issue: { path?: unknown[] } | null) => issue?.path?.[0] === 'email' || issue?.path?.[0] === 'token')
  );
}

export default function ResetPasswordPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});
  const [success, setSuccess] = useState(false);
  // The link looked complete, but the server refused it (wrong, used or expired).
  const [linkRejected, setLinkRejected] = useState(false);
  const panelRef = useRef<HTMLDivElement>(null);

  const linkInvalid = !token || !email || linkRejected;

  // The form disappears on success or on a refused link; move focus to what
  // replaced it so keyboard and screen-reader users are not dropped on <body>.
  useEffect(() => {
    if (success || linkRejected) panelRef.current?.focus();
  }, [success, linkRejected]);

  useEffect(() => {
    if (!success) return undefined;
    const timer = window.setTimeout(() => navigate('/signin', { state: SIGNIN_AFTER_RESET }), REDIRECT_DELAY_MS);
    return () => window.clearTimeout(timer);
  }, [success, navigate]);

  // Opens the forgot-password form on /signin with the address from the link filled in.
  const requestNewLink = () => navigate('/signin', { state: { view: 'forgot', ...(email ? { email } : {}) } });

  const validate = (): boolean => {
    const next: FieldErrors = {};
    if (newPassword.length < 8) {
      next.newPassword = t('auth.passwordTooShort');
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(newPassword)) {
      next.newPassword = t('auth.passwordWeak');
    }
    if (!next.newPassword && newPassword !== confirmPassword) {
      next.confirmPassword = t('auth.passwordsMismatch');
    }
    setFieldErrors(next);
    if (next.newPassword) {
      document.getElementById('reset-new-password')?.focus();
    } else if (next.confirmPassword) {
      document.getElementById('reset-confirm-password')?.focus();
    }
    return !next.newPassword && !next.confirmPassword;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (linkInvalid || !validate()) return;

    setIsLoading(true);
    try {
      const API_BASE_URL = resolveApiBaseUrl();
      const response = await fetch(`${API_BASE_URL}/auth/confirm-reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, token, newPassword }),
      });
      const data = (await response.json().catch(() => ({}))) as { error?: unknown; details?: unknown };

      if (response.ok) {
        setSuccess(true);
      } else if (isLinkRejected(response.status, data)) {
        setLinkRejected(true);
      } else if (response.status === 429) {
        setError(t('auth.tooManyRequests'));
      } else {
        // The API's error text is English; never show it raw on an Arabic page.
        setError(t('auth.passwordUpdateFailed'));
      }
    } catch {
      setError(t('auth.networkError'));
    } finally {
      setIsLoading(false);
    }
  };

  const title = success ? t('auth.passwordUpdatedTitle') : t('auth.resetPasswordTitle');
  const subtitle = success
    ? t('auth.passwordUpdatedSubtitle')
    : linkInvalid
      ? t('auth.resetLinkInvalidSubtitle')
      : t('auth.newPasswordSubtitle');

  return (
    <AuthShell title={title} subtitle={subtitle} backTo="/signin" backLabel={t('auth.backToSignIn')}>
      {error ? <AuthNotice tone="error">{error}</AuthNotice> : null}

      {success ? (
        <div ref={panelRef} tabIndex={-1} className="space-y-5 text-center outline-none">
          <span
            className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-600 dark:bg-emerald-400/10 dark:text-emerald-300"
            aria-hidden="true"
          >
            <CheckCircle2 className="h-8 w-8" />
          </span>
          <p className="text-[0.9375rem] leading-relaxed text-[var(--pv-color-text-muted)]">
            {t('auth.passwordResetSuccess')}
          </p>
          <Button size="lg" className="w-full" onClick={() => navigate('/signin', { state: SIGNIN_AFTER_RESET })}>
            {t('auth.signIn')}
          </Button>
        </div>
      ) : linkInvalid ? (
        <div ref={panelRef} tabIndex={-1} className="space-y-5 outline-none">
          <AuthNotice tone="warning">{t('auth.resetLinkInvalidLong')}</AuthNotice>
          <Button size="lg" className="w-full" onClick={requestNewLink}>
            {t('auth.requestNewLink')}
          </Button>
        </div>
      ) : (
        <>
          <form onSubmit={handleSubmit} noValidate className="space-y-5">
            <AuthField
              id="reset-new-password"
              type={showPassword ? 'text' : 'password'}
              name="newPassword"
              label={t('auth.newPassword')}
              icon={Lock}
              value={newPassword}
              onChange={(e) => {
                setNewPassword(e.target.value);
                if (fieldErrors.newPassword) setFieldErrors((prev) => ({ ...prev, newPassword: undefined }));
              }}
              readOnly={isLoading}
              required
              autoComplete="new-password"
              hint={t('auth.passwordRequirements')}
              error={fieldErrors.newPassword}
              endAdornment={
                <PasswordToggle
                  visible={showPassword}
                  onToggle={() => setShowPassword((v) => !v)}
                  controls="reset-new-password"
                  label={t('auth.showPassword')}
                />
              }
            />

            <AuthField
              id="reset-confirm-password"
              type={showConfirm ? 'text' : 'password'}
              name="confirmPassword"
              label={t('auth.confirmPassword')}
              icon={Lock}
              value={confirmPassword}
              onChange={(e) => {
                setConfirmPassword(e.target.value);
                if (fieldErrors.confirmPassword) setFieldErrors((prev) => ({ ...prev, confirmPassword: undefined }));
              }}
              readOnly={isLoading}
              required
              autoComplete="new-password"
              error={fieldErrors.confirmPassword}
              endAdornment={
                <PasswordToggle
                  visible={showConfirm}
                  onToggle={() => setShowConfirm((v) => !v)}
                  controls="reset-confirm-password"
                  label={t('auth.showPassword')}
                />
              }
            />

            <Button type="submit" size="lg" className="w-full" loading={isLoading}>
              {isLoading ? t('auth.updatingPassword') : (
                <>
                  {t('auth.updatePassword')}
                  {/* Points forward: right in English, mirrored to the left in Arabic. */}
                  <ArrowRight className="h-5 w-5 shrink-0 rtl:-scale-x-100" aria-hidden="true" />
                </>
              )}
            </Button>
          </form>

          <p className="mt-6 border-t border-[var(--pv-border-subtle)] pt-5 text-center text-[0.9375rem] text-[var(--pv-color-text-muted)]">
            {t('auth.rememberedPassword')}{' '}
            <Link to="/signin" className={authLinkClass}>
              {t('auth.signIn')}
            </Link>
          </p>
        </>
      )}
    </AuthShell>
  );
}
